import { useEffect, useState, useRef } from "react";
import Table from "../components/Table";
import Modal from "../components/Modal";
import ModalVerPDF from "../components/ModalVerPDF";

const API_URL = process.env.REACT_APP_API_URL;

export default function AsignacionesScreen({ currentUser, token }) {
    const isAdmin = currentUser?.rol === 'admin';
    const [showBajas, setShowBajas] = useState(false);
    const [asign, setAsign] = useState([]);
    const [loading, setLoading] = useState(true);
    const [modal, setModal] = useState({ visible: false, titulo: '', mensaje: '', tipo: 'info', onConfirm: null });
    const fileInputRef = useRef();
    const [asignSeleccionada, setAsignSeleccionada] = useState(null);

    // ← Nuevo estado para el visor
    const [pdfUrl, setPdfUrl] = useState(null);
    const [pdfTitulo, setPdfTitulo] = useState('');
    const [pdfRow, setPdfRow] = useState(null);

    const showModal = (tipo, titulo, mensaje, onConfirm = null) =>
        setModal({ visible: true, tipo, titulo, mensaje, onConfirm });

    const cargar = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/asignaciones`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json().catch(() => []);
            setAsign(Array.isArray(data) ? data : []);
        } catch {
            showModal("error", "Error", "No se pudieron cargar los datos");
        } finally {
            setLoading(false);
        }
    };

    const cargarBajas = async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_URL}/asignaciones/canceladas`, { headers: { Authorization: `Bearer ${token}` } });
            const data = await res.json().catch(() => []);
            setAsign(Array.isArray(data) ? data : []);
        } catch {
            showModal("error", "Error", "No se pudieron cargar los datos");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => { cargar(); }, []);

    const formatFecha = (fecha) => {
        if (!fecha) return '—';
        const d = new Date(fecha);
        return `${String(d.getUTCDate()).padStart(2, '0')}-${String(d.getUTCMonth() + 1).padStart(2, '0')}-${d.getUTCFullYear()}`;
    };

    // ← Nuevo handler para ver PDF
    const handleVerPDF = async (row) => {
        try {
            const res = await fetch(`${API_URL}/responsivas/ver/${row.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('No se pudo cargar el documento');
            const blob = await res.blob();
            setPdfUrl(URL.createObjectURL(blob));
            setPdfTitulo(`Responsiva — ${row.usuario_nombre ?? ''}`);
            setPdfRow(row);
        } catch (err) {
            showModal('error', 'Error', err.message);
        }
    };

    const cerrarPDF = () => {
        URL.revokeObjectURL(pdfUrl);
        setPdfUrl(null);
        setPdfRow(null);
    };

    const handleDescargar = async (row) => {
        try {
            const res = await fetch(`${API_URL}/responsivas/descargar/${row.id}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            if (!res.ok) throw new Error('No se pudo descargar');
            const blob = await res.blob();
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url; a.download = `responsiva_${row.id}.pdf`; a.click();
            URL.revokeObjectURL(url);
        } catch {
            showModal('error', 'Error', 'No se pudo descargar la responsiva');
        }
    };

    // Descarga desde dentro del visor (usa el row guardado)
    const handleDescargarDesdePDF = () => handleDescargar(pdfRow);

    const handleSubirFirmada = async (archivo) => {
        if (!archivo || !asignSeleccionada) return;
        const formData = new FormData();
        formData.append('responsiva', archivo);
        try {
            const res = await fetch(`${API_URL}/responsivas/firmar/${asignSeleccionada.id}`, {
                method: 'PUT',
                headers: { Authorization: `Bearer ${token}` },
                body: formData
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) throw new Error(data.message);
            setAsignSeleccionada(null);
            cargar();
            showModal('success', 'Listo', 'Responsiva firmada subida correctamente');
        } catch (err) {
            showModal('error', 'Error', err.message);
        }
    };

    const columnas = [
        { header: 'Fecha inicio', render: (row) => formatFecha(row.fecha_inicio) },
        { header: 'Fecha fin', render: (row) => formatFecha(row.fecha_fin) },
        {
            header: 'Estado', render: (row) => {
                const colores = { 'Pendiente': '#007bff', 'Firmada': '#28a745', 'Cancelada': '#dc3545' };
                return <span style={{ padding: '3px 8px', borderRadius: 4, fontSize: 11, color: 'white', background: colores[row.estado] ?? '#6c757d' }}>{row.estado}</span>;
            }
        },
        { header: 'Usuario', accessor: 'usuario_nombre' },
        { header: 'N° Inventario', accessor: 'numero_inventario' },
        { header: 'N° Serie', accessor: 'numero_serie' },
        { header: 'Tipo', accessor: 'tipo_equipo' },
        {
            header: 'Acciones', render: (row) => (
                <>
                    {/* Ver — para todos los roles */}
                    <button className="btn-edit" style={{ background: '#17a2b8', color: 'white' }}
                        onClick={() => handleVerPDF(row)}>
                        Ver
                    </button>

                    {/* Descargar — solo admin */}
                    {isAdmin && (
                        <button className="btn-edit" onClick={() => handleDescargar(row)}>
                            Descargar
                        </button>
                    )}

                    {/* Subir firmada — solo admin y pendiente */}
                    {isAdmin && row.estado === 'Pendiente' && (
                        <button className="btn-edit" style={{ background: '#28a745', color: 'white' }}
                            onClick={() => { setAsignSeleccionada(row); fileInputRef.current.click(); }}>
                            Subir firmada
                        </button>
                    )}
                </>
            )
        },
    ];

    if (loading) return <p>Cargando...</p>;

    return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                <h2 style={{ margin: 0 }}>Asignaciones</h2>
                {isAdmin && (
                    <button className="btn-secondary" onClick={() => {
                        if (showBajas) { setShowBajas(false); cargar(); }
                        else { setShowBajas(true); cargarBajas(); }
                    }}>
                        {showBajas ? 'Ver Activas' : 'Ver Canceladas'}
                    </button>
                )}
            </div>

            <input ref={fileInputRef} type="file" accept="application/pdf" style={{ display: 'none' }}
                onChange={(e) => {
                    const archivo = e.target.files[0];
                    if (archivo) handleSubirFirmada(archivo);
                    e.target.value = '';
                }} />

            <Table columnas={columnas} datos={asign} mensajeVacio="Sin asignaciones" />
            <Modal modal={modal} setModal={setModal} />

            {/* Visor PDF */}
            <ModalVerPDF
                url={pdfUrl}
                titulo={pdfTitulo}
                isAdmin={isAdmin}
                onDescargar={handleDescargarDesdePDF}
                onClose={cerrarPDF}
            />
        </div>
    );
}